// Shared card-image renderer. Single-faced cards render one <img>. Double-
// faced cards (transform / modal_dfc) render BOTH faces side by side, so the
// front and back are visible at once — no flip interaction needed.

import { cn } from "../lib/cn";

interface Props {
  front: string;
  /** Back-face image; when present, both faces render side by side. */
  back?: string | null;
  alt: string;
  className?: string;
  /** Image loading hint; "eager" for hover popups, "lazy" for grids. */
  loading?: "eager" | "lazy";
  /** Whether the image can be dragged (false for 3D/galaxy tiles). */
  draggable?: boolean;
}

export default function CardImage({
  front,
  back,
  alt,
  className,
  loading = "eager",
  draggable = true,
}: Props) {
  if (!back) {
    return (
      <img
        src={front}
        alt={alt}
        loading={loading}
        draggable={draggable}
        className={cn("block w-full rounded-md", className)}
      />
    );
  }

  // Side-by-side: each face takes an equal share of the available width.
  return (
    <div className="flex gap-2 w-full">
      <img
        src={front}
        alt={alt}
        loading={loading}
        draggable={draggable}
        className={cn("block min-w-0 flex-1 rounded-md", className)}
      />
      <img
        src={back}
        alt={`${alt} (back face)`}
        loading={loading}
        draggable={draggable}
        className={cn("block min-w-0 flex-1 rounded-md", className)}
      />
    </div>
  );
}
